import { compareAiAudienceBudgets, describeBudgetGate, getBuilderState } from '@/utils/audience-builder'
import { formatCurrencyAutoLocale } from '@/utils/tools'

/**
 * Reminds which acquisition budget the AI audiences were planned for and what that budget supports, and
 * warns when the budgets set on this step no longer match that plan. `budgets` is the Budget step state.
 */
const AiAudienceBudgetNote = ({ getStepContentData, currency, budgets }) => {
    const salesFunnelData = getStepContentData('salesFunnelSetup')
    const builder = getBuilderState(salesFunnelData)
    if (builder.mode !== 'ai' || !builder.set) return null

    const format = (minorUnits) => formatCurrencyAutoLocale({ amount: minorUnits / 100, currency })
    const gate = describeBudgetGate(builder.set)
    const plannedBudget = builder.set.inputs?.acquisitionBudget
    const lines = [
        plannedBudget ? `AI audiences were planned for ${format(plannedBudget)} a day for acquisition.` : null,
        gate.summary,
        ...gate.warnings,
    ].filter(Boolean)

    const comparison = compareAiAudienceBudgets(salesFunnelData, budgets)
    const mismatches = []
    if (comparison?.differsFromPlan) {
        mismatches.push(
            `The AI audiences now get ${format(comparison.total)} a day in total. Rebuild them in Sales Funnel Setup for this budget, or change the budgets here.`,
        )
    }
    if (comparison?.belowRequired) {
        mismatches.push(
            `Some AI audiences get less than ${format(comparison.required)} a day, the budget each one needs to finish learning.`,
        )
    }

    // With one campaign budget the amounts of the existing audiences are not known here, so no combined line
    const combined =
        comparison?.existingCount > 0
            ? `AI audiences ${format(comparison.total)} / existing audiences ${format(comparison.existingTotal)} / total ${format(comparison.total + comparison.existingTotal)}`
            : null

    return (
        <div
            className="tw:text-sm tw:max-w-[222px] tw:pt-8 tw:flex tw:flex-col tw:gap-2"
            data-testid="ai-audience-budget-note"
        >
            {lines.map((line) => (
                <span key={line}>{line}</span>
            ))}
            {combined && <span>{combined}</span>}
            {mismatches.map((line) => (
                <span key={line} className="tw:text-yellow-300" role="status">
                    {line}
                </span>
            ))}
        </div>
    )
}

export default AiAudienceBudgetNote
